import { ListApiResponse } from '../types/apiResponse';
import { UploadModel } from '../types/apiUtils';
import {
  AddModel,
  ArchiveModel,
  AuditItem,
  GameAddViewModel,
  GameFranchiseModel,
  GameItemDto,
  GameItemModel,
  GameSkuViewModel,
  GameViewModel,
  IdResponse,
  KeyRequestMetricsAggrgatesDto,
  KeyRequestMetricsPerDayDto,
  SKUCloneModel,
  SKUItemDto,
  StatsDTO,
  UnarchiveModel,
  GameFranchiseAddViewModel,
} from '../types/swagger-dtos/GamesDtos';
import {
  apiUrl,
  getList,
  deleteRecord,
  getSingleRecord,
  saveRecord,
  getStatsData,
  getAll,
  postAction,
  download,
  upload,
  getAuditData,
  exportData,
  get,
  putAction,
} from './apiUtils';
import type { ApiListParamsWithUnderscores, ApiExportParams } from '../types/api/listParams';
const baseUrl = apiUrl + '/games/';

export function getGames(params?: ApiListParamsWithUnderscores): Promise<ListApiResponse<GameItemModel>> {
  return getList(`${baseUrl}search`, params);
}

export function getGamesWithSku(params?: ApiListParamsWithUnderscores): Promise<ListApiResponse<GameSkuViewModel>> {
  return getList(`${apiUrl}/gameskus/search`, params);
}

export function getSkus(
  gameId: string,
  params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<GameSkuViewModel>> {
  return getList(`${baseUrl}${gameId}/skus`, params);
}

export function getGameById(id: string): Promise<GameViewModel> {
  return getSingleRecord<GameViewModel>(baseUrl, id);
}

export function saveGame(game: GameAddViewModel): Promise<GameItemDto> {
  return saveRecord(baseUrl, game);
}

export function archiveGame(
  id: string,
  lockSignOff: boolean
): Promise<GameItemDto> {
  const deleteModel: ArchiveModel = { lockSignOff: lockSignOff };
  return deleteRecord(baseUrl, id, deleteModel);
}

export function archiveGameSku(
  gameId: string,
  skuId: string,
  lockSignOff: boolean
): Promise<SKUItemDto> {
  return deleteRecord(`${baseUrl}${gameId}/sku/`, skuId, { lockSignOff });
}

export function unarchiveGame(
  id: string,
  restorePendingRequests: boolean
): Promise<GameItemDto> {
  const unarchiveModel: UnarchiveModel = {
    restorePendingRequests: restorePendingRequests,
  };
  return putAction(`${baseUrl}${id}/unarchive`, unarchiveModel);
}

export function unarchiveGameSku(
  gameId: string,
  skuId: string,
  restorePendingRequests: boolean
): Promise<SKUItemDto> {
  const unarchiveModel: UnarchiveModel = {
    restorePendingRequests: restorePendingRequests,
  };
  return putAction(
    `${baseUrl}${gameId}/sku/${skuId}/unarchive`,
    unarchiveModel
  );
}

export function getStats(id: string): Promise<StatsDTO> {
  return getStatsData(baseUrl, id);
}

export function getStatsByDay(
  id: string
): Promise<KeyRequestMetricsPerDayDto[]> {
  return get(`${baseUrl}${id}/statsByDay/`);
}

export function getAggregateStats(
  id: string,
  from: Date,
  to: Date
): Promise<KeyRequestMetricsAggrgatesDto> {
  // Format the from as YYYY-MM-DD
  const fromStr = from.toISOString().split('T')[0];
  // Format the to as YYYY-MM-DD
  const toStr = to.toISOString().split('T')[0];

  return get(
    `${baseUrl}${id}/aggregateStats?fromDate=${fromStr}&toDate=${toStr}`
  );
}

export function getAudit(id: string): Promise<AuditItem> {
  return getAuditData(baseUrl, id);
}

export function addAudit(id: string, comment: string): Promise<AuditItem> {
  const auditAddModel: AddModel = {
    comment: comment,
  };
  return postAction(`${baseUrl}${id}/audit`, auditAddModel);
}

export function getAllGames(): Promise<GameItemDto[]> {
  return getAll(baseUrl);
}

export function cloneGameSku({
  gameId,
  skuId,
  skuName,
  packageId,
  withRestrictions,
}: {
  gameId: string;
  skuId: string;
  skuName: string;
  packageId?: string;
  withRestrictions: boolean;
}): Promise<string> {
  const addModel: SKUCloneModel = {
    skuName: skuName,
    packageId: packageId,
    withRestrictions: withRestrictions,
  };
  return postAction(`${baseUrl}${gameId}/sku/${skuId}/clone`, addModel);
}

export function downloadGames(): Promise<void> {
  return download(`${baseUrl}download`);
}
export function downloadTemplate(): Promise<void> {
  return download(`${baseUrl}download/template`);
}
export function uploadGames(record: UploadModel): Promise<IdResponse> {
  return upload(`${baseUrl}upload`, record);
}

export function exportGameListData(params: ApiExportParams): Promise<void> {
  return exportData(`${apiUrl}/export/games/search`, 'gameName', params);
}

export function getGameFranchises(params?: ApiListParamsWithUnderscores): Promise<ListApiResponse<GameFranchiseModel>> {
  return getList(`${baseUrl}franchise/search`, params);
}

export function saveGameFranchise(
  franchise: GameFranchiseAddViewModel & { id?: string }
): Promise<GameFranchiseModel> {
  if (franchise.id) {
    return putAction(`${baseUrl}franchise/${franchise.id}`, franchise);
  } else {
    return postAction(`${baseUrl}franchise`, franchise);
  }
}

export function getAllGameFranchises(): Promise<GameFranchiseModel[]> {
  return getAll(`${baseUrl}franchise`);
}

export function deleteGameFranchise(id: string): Promise<void> {
  return deleteRecord(`${baseUrl}franchise/`, id);
}
